/**
 * Importar extracto: pregunta al servidor qué entraría (sin guardar nada) y
 * en qué día está el saldo inicial de la cuenta, para separar lo nuevo de lo
 * repetido y de lo que ya cuenta en ese saldo.
 */
import { send } from '@actual-app/core/platform/client/connection';
import { q } from '@actual-app/core/shared/query';

import { aqlQuery } from '#queries/aqlQuery';

import { aImportar } from './importar';
import type { EntradaVistaPrevia } from './importar';
import type { MovimientoLeido } from './leerExtracto';
import { rangoBusqueda } from './parecidos';
import type { Apuntado } from './parecidos';

export type VistaPrevia = {
  entradas: EntradaVistaPrevia[];
  fechaSaldoInicial: string | null;
  /**
   * Lo apuntado a mano (en cualquier cuenta del presupuesto) cerca de las
   * fechas del extracto: candidatos a «¿Ya lo tenías?». El servidor solo
   * empareja la misma línea del banco; esto lo decide la persona.
   */
  apuntados: Apuntado[];
};

/** null si el servidor no lo acepta. */
export async function pedirVistaPrevia(
  cuenta: string,
  movimientos: MovimientoLeido[],
): Promise<VistaPrevia | null> {
  const { errors = [], updatedPreview = [] } = await send(
    'transactions-import',
    {
      accountId: cuenta,
      transactions: aImportar(cuenta, movimientos),
      isPreview: true,
      opts: { matchOnlySameBankLine: true },
    },
  );
  if (errors.length > 0) {
    return null;
  }
  // Lo de antes del saldo inicial ya cuenta en él: no se trae.
  const { data: apertura } = await aqlQuery(
    q('transactions')
      .filter({ account: cuenta, starting_balance_flag: true })
      .select(['date'])
      .orderBy({ date: 'asc' })
      .limit(1),
  );
  const entradas = updatedPreview as unknown as EntradaVistaPrevia[];
  return {
    entradas,
    fechaSaldoInicial: apertura?.[0]?.date ?? null,
    apuntados: await buscarApuntados(movimientos, entradas),
  };
}

type FilaApuntado = {
  id: string;
  date: string;
  amount: number;
  account: string;
  payee: string | null;
  category: string | null;
  notes: string | null;
  imported_payee: string | null;
  transfer_id: string | null;
  is_parent: boolean;
  is_child: boolean;
  starting_balance_flag: boolean;
};

async function buscarApuntados(
  movimientos: MovimientoLeido[],
  entradas: EntradaVistaPrevia[],
): Promise<Apuntado[]> {
  const rango = rangoBusqueda(movimientos.map(m => m.fecha));
  if (!rango) {
    return [];
  }
  const emparejados = new Set(
    entradas
      .map(e => (e.existing as { id?: string } | undefined)?.id)
      .filter(Boolean),
  );
  const { data = [] } = await aqlQuery(
    q('transactions')
      .filter({
        date: { $gte: rango.desde, $lte: rango.hasta },
        'account.offbudget': false,
        'account.closed': false,
      })
      .select([
        'id',
        'date',
        'amount',
        'account',
        'payee',
        'category',
        'notes',
        'imported_payee',
        'transfer_id',
        'is_parent',
        'is_child',
        'starting_balance_flag',
      ])
      .options({ splits: 'all' }),
  );
  return (data as FilaApuntado[])
    .filter(
      t =>
        // Solo lo apuntado a mano: lo que vino de un banco o de otro
        // extracto ya es una cifra del banco, y dos iguales son dos cobros.
        !t.imported_payee &&
        !t.transfer_id &&
        !t.is_parent &&
        !t.is_child &&
        !t.starting_balance_flag &&
        !emparejados.has(t.id),
    )
    .map(t => ({
      id: t.id,
      fecha: t.date,
      importe: t.amount,
      cuenta: t.account,
      payee: t.payee,
      categoria: t.category,
      notas: t.notes,
    }));
}
